'use client'

import { useCookieConsent, CookiePreferences } from './CookieConsentContext'
import { useState } from 'react'
import Link from 'next/link'

export default function CookieBanner() {
  const {
    showBanner,
    showSettings,
    openSettings,
    closeSettings,
    acceptAll,
    rejectAll,
    savePreferences,
  } = useCookieConsent()

  const [prefs, setPrefs] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    functionality: false,
    marketing: false,
  })

  if (!showBanner) {
    return null
  }

  const togglePref = (key: keyof CookiePreferences) => {
    if (key === 'necessary') return // No se puede desactivar
    setPrefs((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  return (
    <>
      {/* Overlay sutil cuando se muestran las settings */}
      {showSettings && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
          onClick={closeSettings}
          aria-hidden="true"
        />
      )}

      {/* Banner principal */}
      {!showSettings && (
        <div
          role="dialog"
          aria-live="polite"
          aria-label="Aviso de cookies"
          className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6 animate-slide-up"
        >
          <div className="container mx-auto max-w-5xl">
            <div className="bg-gray-900/95 backdrop-blur-md border border-white/20 rounded-2xl shadow-2xl p-5 sm:p-7">
              <div className="flex flex-col sm:flex-row sm:items-start sm:gap-6">
                {/* Icono + Texto */}
                <div className="flex-1 mb-4 sm:mb-0">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl" aria-hidden="true">🍪</span>
                    <h2 className="text-lg sm:text-xl font-bold text-white">
                      Cuidamos tu privacidad
                    </h2>
                  </div>
                  <p className="text-white/80 text-sm leading-relaxed">
                    Usamos cookies propias y de terceros para que el sitio funcione, recordar tus
                    preferencias, analizar el tráfico y mostrar contenido personalizado. Puedes
                    aceptar todas las cookies, rechazar las opcionales o configurar tus preferencias.
                    {' '}
                    <Link
                      href="/politica-de-cookies"
                      className="text-blue-400 hover:text-blue-300 underline"
                    >
                      Más información
                    </Link>
                  </p>
                </div>

                {/* Botones */}
                <div className="flex flex-col gap-2 sm:min-w-[180px]">
                  <button
                    onClick={acceptAll}
                    className="px-5 py-2.5 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg transition-colors text-sm"
                  >
                    Aceptar todas
                  </button>
                  <button
                    onClick={rejectAll}
                    className="px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg border border-white/30 transition-colors text-sm"
                  >
                    Solo necesarias
                  </button>
                  <button
                    onClick={openSettings}
                    className="px-5 py-2 text-blue-400 hover:text-blue-300 font-medium text-sm underline"
                  >
                    Configurar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de configuración */}
      {showSettings && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Configurar preferencias de cookies"
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          <div className="bg-gray-900 border border-white/20 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 sm:p-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
                  <span aria-hidden="true">🍪</span>
                  Configurar cookies
                </h2>
                <button
                  onClick={closeSettings}
                  className="text-white/60 hover:text-white text-2xl"
                  aria-label="Cerrar"
                >
                  ×
                </button>
              </div>

              <p className="text-white/80 text-sm mb-6">
                Marca las categorías de cookies que deseas permitir. Las cookies necesarias son
                imprescindibles para el funcionamiento del sitio y no se pueden desactivar.
              </p>

              <div className="space-y-4">
                {/* Necesarias */}
                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <h3 className="text-white font-semibold mb-1">Necesarias</h3>
                      <p className="text-white/70 text-xs">
                        Imprescindibles para el funcionamiento del sitio (sesión, seguridad, recordar
                        tu decisión de cookies). No se pueden desactivar.
                      </p>
                    </div>
                    <div className="flex items-center">
                      <div className="w-10 h-6 bg-blue-500 rounded-full relative cursor-not-allowed opacity-70">
                        <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full"></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Analíticas */}
                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <h3 className="text-white font-semibold mb-1">Analíticas y rendimiento</h3>
                      <p className="text-white/70 text-xs">
                        Nos permiten medir el número de visitantes y cómo navegas por el sitio
                        (Google Analytics). Toda la información es agregada y anónima.
                      </p>
                    </div>
                    <button
                      onClick={() => togglePref('analytics')}
                      aria-label="Activar o desactivar cookies analíticas"
                      className="flex items-center"
                    >
                      <div
                        className={`w-10 h-6 rounded-full relative transition-colors ${
                          prefs.analytics ? 'bg-blue-500' : 'bg-white/20'
                        }`}
                      >
                        <div
                          className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            prefs.analytics ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        ></div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Funcionalidad */}
                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <h3 className="text-white font-semibold mb-1">Funcionalidad</h3>
                      <p className="text-white/70 text-xs">
                        Permiten recordar tus preferencias (idioma, región) para ofrecer una
                        experiencia personalizada.
                      </p>
                    </div>
                    <button
                      onClick={() => togglePref('functionality')}
                      aria-label="Activar o desactivar cookies de funcionalidad"
                      className="flex items-center"
                    >
                      <div
                        className={`w-10 h-6 rounded-full relative transition-colors ${
                          prefs.functionality ? 'bg-blue-500' : 'bg-white/20'
                        }`}
                      >
                        <div
                          className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            prefs.functionality ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        ></div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Marketing */}
                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <h3 className="text-white font-semibold mb-1">Publicidad y marketing</h3>
                      <p className="text-white/70 text-xs">
                        Se utilizan para mostrar anuncios relevantes y medir la eficacia de nuestras
                        campañas (Meta Pixel, Google Ads).
                      </p>
                    </div>
                    <button
                      onClick={() => togglePref('marketing')}
                      aria-label="Activar o desactivar cookies de marketing"
                      className="flex items-center"
                    >
                      <div
                        className={`w-10 h-6 rounded-full relative transition-colors ${
                          prefs.marketing ? 'bg-blue-500' : 'bg-white/20'
                        }`}
                      >
                        <div
                          className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            prefs.marketing ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        ></div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Botones del modal */}
              <div className="flex flex-col sm:flex-row gap-2 mt-6">
                <button
                  onClick={() => savePreferences(prefs)}
                  className="flex-1 px-5 py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg transition-colors"
                >
                  Guardar preferencias
                </button>
                <button
                  onClick={acceptAll}
                  className="flex-1 px-5 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg border border-white/30 transition-colors"
                >
                  Aceptar todas
                </button>
              </div>

              <p className="text-white/50 text-xs text-center mt-4">
                Más información en nuestra{' '}
                <Link
                  href="/politica-de-cookies"
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  Política de Cookies
                </Link>
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
